import { useRef } from 'react';
import type { DragEvent, KeyboardEvent, MouseEvent } from 'react';
import { DOCUMENT_TYPE_BY_CODE } from '@erp-attach/shared';
import type { DocumentSummary } from '../api/types';
import { formatBytes, formatDate, formatDateTime, shortUpn } from '../lib/format';
import { Button } from './ui/Button';
import { Chip, StatusChip } from './ui/Chip';
import { Icon } from './ui/Icon';
import { FilePlate } from './FilePlate';
import { setDragData } from '../lib/documentDrag';

export type ViewMode = 'list' | 'grid';

export interface DocumentListProps {
  documents: readonly DocumentSummary[];
  view: ViewMode;
  selectedId: string | null;
  /** Single click / Enter: show in the peek panel. */
  onSelect: (doc: DocumentSummary) => void;
  /** Double click / "Open": the full viewer page. */
  onOpen: (doc: DocumentSummary) => void;
  onDownload: (doc: DocumentSummary) => void;
  busyId?: string | null;
  /** Right-click, Shift+F10 or the menu key: open the file's action menu at a viewport point. */
  onContextMenu?: (doc: DocumentSummary, at: { x: number; y: number }) => void;
  /** F2 on a focused file. */
  onRename?: (doc: DocumentSummary) => void;
  /** Drag start (the file) and end (null); rows are draggable only when this is set. */
  onDragDoc?: (doc: DocumentSummary | null) => void;
  /** The short link carried by a drag, for drops outside the app. */
  linkFor?: (doc: DocumentSummary) => string;
}

export function typeLabel(code: string): string {
  return DOCUMENT_TYPE_BY_CODE.get(code)?.label ?? code;
}

export function isViewableSummary(doc: DocumentSummary): boolean {
  return doc.actions.includes('read') && (doc.status === 'available' || doc.status === 'hidden' || doc.hidden);
}

/**
 * The files in one folder. Arrow keys move between rows, Enter selects, Space downloads nothing
 * (plain focus), and the whole row is the click target so the peek panel feels like a drive.
 */
export function DocumentList({ documents, view, selectedId, onSelect, onOpen, onDownload, busyId, onContextMenu, onRename, onDragDoc, linkFor }: DocumentListProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Shared by list rows and grid tiles: drag to move (or out as a link), right-click / keyboard menu, F2.
  const rowHandlers = (doc: DocumentSummary) => ({
    'data-doc-id': doc.id,
    draggable: !!onDragDoc,
    onDragStart: (e: DragEvent<HTMLElement>) => {
      if (!onDragDoc) return;
      setDragData(e.dataTransfer, doc, linkFor?.(doc) ?? '');
      onDragDoc(doc);
    },
    onDragEnd: () => onDragDoc?.(null),
    onContextMenu: (e: MouseEvent<HTMLElement>) => {
      if (!onContextMenu) return;
      e.preventDefault();
      onSelect(doc);
      onContextMenu(doc, { x: e.clientX, y: e.clientY });
    },
    onKeyDownCapture: (e: KeyboardEvent<HTMLElement>) => {
      if (onContextMenu && (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey))) {
        e.preventDefault();
        const r = e.currentTarget.getBoundingClientRect();
        onContextMenu(doc, { x: r.left + 24, y: r.bottom });
      } else if (onRename && e.key === 'F2') {
        e.preventDefault();
        onRename(doc);
      }
    },
  });

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[data-doc-row]') ?? []);
    if (items.length === 0) return;
    const current = items.findIndex((el) => el === document.activeElement || el.contains(document.activeElement));
    const vertical = view === 'list' ? 1 : columnsOf(rootRef.current);
    let next = -1;
    if (e.key === 'ArrowDown') next = Math.min(items.length - 1, (current < 0 ? -1 : current) + vertical);
    else if (e.key === 'ArrowUp') next = Math.max(0, (current < 0 ? items.length : current) - vertical);
    else if (view === 'grid' && e.key === 'ArrowRight') next = Math.min(items.length - 1, current + 1);
    else if (view === 'grid' && e.key === 'ArrowLeft') next = Math.max(0, current - 1);
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    if (next >= 0) {
      e.preventDefault();
      items[next]?.focus();
    }
  };

  if (view === 'grid') {
    return (
      <div ref={rootRef} className="gsl-file-grid" role="listbox" aria-label="Files" onKeyDown={onKeyDown}>
        {documents.map((doc) => {
          const v = doc.currentVersion;
          const selected = doc.id === selectedId;
          return (
            <button
              key={doc.id}
              type="button"
              role="option"
              aria-selected={selected}
              className="gsl-file-card"
              data-doc-row
              {...rowHandlers(doc)}
              onClick={() => onSelect(doc)}
              onDoubleClick={() => onOpen(doc)}
            >
              <div className="flex w-full items-start justify-between gap-2">
                <FilePlate filename={v?.originalFilename} mime={v?.detectedMime} size="lg" />
                <StatusChip status={doc.status} hidden={doc.hidden} />
              </div>
              <span className="w-full">
                <span className="block truncate text-sm font-medium" title={doc.title}>
                  {doc.title}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{typeLabel(doc.documentTypeCode)}</span>
              </span>
              <span className="flex w-full items-center justify-between text-xs text-muted-foreground">
                <span>{v ? formatDate(v.uploadedAt) : '—'}</span>
                <span className="gsl-num">{v ? formatBytes(v.sizeBytes) : ''}</span>
              </span>
              {doc.legalHold ? <Chip variant="danger">Legal hold</Chip> : null}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="overflow-x-auto" onKeyDown={onKeyDown}>
      <table className="w-full text-sm" aria-label="Files">
        <thead>
          <tr>
            <th className="gsl-th w-full">Name</th>
            <th className="gsl-th gsl-col-mid">Type</th>
            <th className="gsl-th gsl-col-narrow">Status</th>
            <th className="gsl-th gsl-col-wide">Uploaded</th>
            <th className="gsl-th gsl-col-wide text-right">Size</th>
            <th className="gsl-th">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => {
            const v = doc.currentVersion;
            const selected = doc.id === selectedId;
            const viewable = isViewableSummary(doc);
            const busy = busyId === doc.id;
            return (
              <tr
                key={doc.id}
                className="gsl-row cursor-pointer border-b border-border last:border-b-0"
                aria-selected={selected}
                data-doc-row
                tabIndex={0}
                {...rowHandlers(doc)}
                onClick={() => onSelect(doc)}
                onDoubleClick={() => onOpen(doc)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (e.ctrlKey || e.metaKey) onOpen(doc);
                    else onSelect(doc);
                  }
                }}
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-3">
                    <FilePlate filename={v?.originalFilename} mime={v?.detectedMime} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium" title={doc.title}>
                          {doc.title}
                        </span>
                        {doc.legalHold ? <Chip variant="danger">Legal hold</Chip> : null}
                      </div>
                      {v ? (
                        <div className="truncate text-xs text-muted-foreground" title={v.originalFilename}>
                          {v.originalFilename}
                          {v.versionNo > 1 ? <span className="gsl-num"> · v{v.versionNo}</span> : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td className="gsl-col-mid whitespace-nowrap px-3 py-2 text-muted-foreground">{typeLabel(doc.documentTypeCode)}</td>
                <td className="gsl-col-narrow px-3 py-2">
                  <StatusChip status={doc.status} hidden={doc.hidden} />
                </td>
                <td className="gsl-col-wide whitespace-nowrap px-3 py-2">
                  {v ? (
                    <>
                      <div>{shortUpn(v.uploadedByUpn)}</div>
                      <div className="text-xs text-muted-foreground">{formatDateTime(v.uploadedAt)}</div>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="gsl-col-wide gsl-num whitespace-nowrap px-3 py-2 text-right text-muted-foreground">{v ? formatBytes(v.sizeBytes) : '—'}</td>
                <td className="px-2 py-2">
                  <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="maximize"
                      iconOnly
                      aria-label={`Open ${doc.title}`}
                      title="Open"
                      disabled={!viewable || busy}
                      onClick={() => onOpen(doc)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="download"
                      iconOnly
                      aria-label={`Download ${doc.title}`}
                      title="Download"
                      disabled={!viewable || !doc.actions.includes('download') || busy}
                      busy={busy}
                      onClick={() => onDownload(doc)}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {documents.length > 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">
          <Icon name="info" size={12} className="mr-1 inline-block align-[-2px]" />
          Click a file to preview it. Double-click or Ctrl+Enter opens it. Right-click (or Shift+F10) for more; drag it onto a folder to move it.
        </p>
      ) : null}
    </div>
  );
}

function columnsOf(root: HTMLElement | null): number {
  if (!root) return 1;
  const cols = getComputedStyle(root).gridTemplateColumns.split(' ').filter(Boolean).length;
  return Math.max(1, cols);
}
